import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsTreemap from 'highcharts/modules/treemap';
import { Unit } from '@/types';
import { renderVisualisationTooltip } from '@/components/visualisation-tooltip';
import { TreemapChart } from './disclosure-treemap.styles';

// Enable the treemap series type. Idempotent + self-initialising so the
// component works in the app, Storybook, and Vitest without each environment
// wiring the module up (mirrors app.tsx's NoDataToDisplay(Highcharts)).
HighchartsTreemap(Highcharts);

// The font stack the app's other charts use (emissions-by-sector-source,
// scenario-outputs) — set on the chart so the group labels match them.
// eslint-disable-next-line max-len
const CHART_FONT_FAMILY = `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`;

// A two-level treemap for the Community report. Outer boxes are `groups`
// (each with its own base colour); inner boxes are their `children`, sized by
// CO₂-e `value` and auto-shaded as brightness tints of the parent via
// Highcharts' `colorVariation`. Presentational only — the page maps the
// payload (bySource / byActivity) into this shape (label + palette lookup);
// nothing is computed or aggregated here. Used by both the Overview page
// (Source → Activity) and the per-source page (Activity → MeasureClass).
const DisclosureTreemap = (props: DisclosureTreemapProps) => {
  const { groups, unit, height = 280, onSelect } = props;

  const options = useMemo(() => {
    // Parents carry id + name + colour (value is summed from children by
    // Highcharts); leaves carry the value and inherit the parent colour,
    // varied by the level-2 colorVariation below.
    const data = [
      ...groups.map((g) => ({ id: g.key, name: g.label, color: g.color })),
      ...groups.flatMap((g) =>
        g.children.map((c) => ({
          id: `${g.key}/${c.key}`,
          name: c.label,
          parent: g.key,
          value: c.value,
        })),
      ),
    ];
    // Resolve a hovered leaf back to its group (for the tooltip title).
    const groupByKey = new Map(groups.map((g) => [g.key, g]));
    return {
      chart: {
        type: 'treemap',
        height,
        animation: false,
        // Group labels share the other charts' font.
        style: { fontFamily: CHART_FONT_FAMILY },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      // App-standard popover (mirrors emissions-by-sector-source): a
      // chromeless Highcharts tooltip wrapping the shared bungle card. A leaf
      // titles with its group; a parent titles itself.
      tooltip: {
        useHTML: true,
        backgroundColor: 'rgba(255,255,255,0)',
        borderWidth: 0,
        shadow: false,
        animation: false,
        hideDelay: 0,
        outside: true,
        padding: 0,
        formatter(): any {
          // Non-shared tooltip: `this` is the tooltip context object and the
          // hovered point hangs off `this.point` (its name/value/parent/color
          // live there, not on `this`). A leaf titles with its group; a parent
          // (no `parent`) titles itself.
          const point: any = (this as any).point;
          const parent = point.parent
            ? groupByKey.get(point.parent)
            : undefined;
          return renderVisualisationTooltip({
            title: parent ? parent.label : point.name,
            entries: [
              {
                color: point.color,
                label: point.name,
                value: point.value,
                unit,
              },
            ],
          });
        },
      },
      series: [
        {
          type: 'treemap',
          layoutAlgorithm: 'squarified',
          allowTraversingTree: false,
          animation: false,
          borderColor: '#ffffff',
          borderWidth: 2,
          dataLabels: { enabled: false },
          // When the page supplies `onSelect`, every box becomes a deep-link to
          // its GROUP: clicking a leaf reports its parent, clicking the group
          // reports itself. The cursor turns to a pointer to advertise it.
          // Without `onSelect` the treemap stays inert (the per-source treemap
          // has no deeper page to navigate to).
          cursor: onSelect ? 'pointer' : undefined,
          // SPREAD the `point` key in only when there's an onSelect — do NOT set
          // it to `undefined` otherwise. Highcharts defaults `series.point` to
          // `{ events: {} }`, and `firePointEvent` reads `series.options.point.
          // events[type]` with no guard; an explicit `point: undefined` clobbers
          // that default, so every hover/mouseout on an inert treemap throws
          // "Cannot read properties of undefined (reading 'events')" (caught by
          // React Router → blank page). Omitting the key keeps Highcharts' safe
          // default. (This was the real navigate-away crash — the per-source
          // treemap has no onSelect, so moving the cursor off it crashed.)
          ...(onSelect && {
            point: {
              events: {
                click(this: any) {
                  onSelect(this.parent ?? this.id);
                },
              },
            },
          }),
          levels: [
            {
              // Group (source / activity) labels only. Fixed dark text on a
              // translucent white chip: readable on any box colour and never
              // flips with the background (Highcharts' default auto-contrast
              // otherwise swaps black/white per box).
              level: 1,
              borderWidth: 4,
              dataLabels: {
                enabled: true,
                align: 'left',
                verticalAlign: 'top',
                backgroundColor: 'rgba(255,255,255,0.85)',
                borderRadius: 3,
                padding: 3,
                style: {
                  color: 'rgba(0,0,0,0.85)',
                  fontWeight: '500',
                  fontSize: '12px',
                  textOutline: 'none',
                },
              },
            },
            {
              // Leaves: colour only, no text -- it cramps/overlaps in small
              // boxes; identity comes from the hover popover instead.
              level: 2,
              colorVariation: { key: 'brightness', to: 0.4 },
              dataLabels: { enabled: false },
            },
          ],
          data,
        },
      ],
    };
  }, [groups, unit, height, onSelect]);

  return (
    <TreemapChart>
      {/* `immutable`: recreate the chart when `options` changes instead of
          mutating it in place. Highcharts 10's incremental treemap update
          (chart.update → series.setData → updateData → removePoint) crashes
          ("point is undefined" in firePointEvent) whenever the parent/child
          point set changes under it — navigating between source pages, a
          simulate redraw, or just a new `groups` array identity. Recreating
          sidesteps that path entirely; animation is already off so there's no
          visible cost, and the memoised `options` (+ memoised inputs upstream)
          keep recreation to real data changes. */}
      <HighchartsReact highcharts={Highcharts} options={options} immutable />
    </TreemapChart>
  );
};

export type DisclosureTreemapLeaf = {
  key: string;
  label: string;
  value: number;
};

export type DisclosureTreemapGroup = {
  key: string;
  label: string;
  color: string;
  children: DisclosureTreemapLeaf[];
};

export type DisclosureTreemapProps = {
  groups: DisclosureTreemapGroup[];
  unit: Unit;
  height?: number;
  // When provided, boxes become clickable: clicking any box (a group or one of
  // its leaves) invokes `onSelect` with the GROUP key (a leaf reports its
  // parent), and the cursor shows a pointer. The Overview treemap uses this to
  // deep-link to the clicked source's page; the per-source treemap omits it
  // (no deeper page exists), so it stays inert.
  onSelect?: (groupKey: string) => void;
};

export { DisclosureTreemap };
